import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSnapshot } from "valtio";
import Blob from "./components/blob";
import Controls from "./components/controls";
import Header from "./components/Header";
import UserAvatar from "./components/UserAvatar";
import { VoiceWebSocketProvider } from "./components/VoiceWebSocketProvider";
import {
  type Speaker,
  voiceAgentStore,
} from "../../../../store/voiceAgentStore";
import TranscriptDisplay from "./components/TranscriptDisplay";
import { Captions, CaptionsOff } from "lucide-react";

const IDLE_RELEASE_DELAY_MS = 120;
const DEFAULT_TRANSITION_DURATION_MS = 600;
const TRANSITION_DURATION_MS: Record<
  Speaker,
  Partial<Record<Speaker, number>>
> = {
  idle: {
    user: 260,
    ai: 320,
  },
  user: {
    idle: 920,
    ai: 320,
  },
  ai: {
    idle: 900,
    user: 260,
  },
};

const getTransitionDuration = (from: Speaker, to: Speaker): number => {
  if (from === to) {
    return 0;
  }
  return TRANSITION_DURATION_MS[from]?.[to] ?? DEFAULT_TRANSITION_DURATION_MS;
};

type TransitionStyle = CSSProperties & {
  "--bg-transition-duration": string;
};

const FiBiVoiceAgent = () => {
  const { speaker, status, showTranscript } = useSnapshot(voiceAgentStore);
  const toggleShowTranscript = () => {
    voiceAgentStore.showTranscript = !voiceAgentStore.showTranscript;
  };
  const [visualSpeaker, setVisualSpeaker] = useState<Speaker>("idle");
  const [transitionDuration, setTransitionDuration] = useState<number>(
    DEFAULT_TRANSITION_DURATION_MS
  );
  const releaseTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
  const frameRef = useRef<number | null>(null);
  const visualSpeakerRef = useRef<Speaker>("idle");

  const clearVisualTimers = useCallback(() => {
    if (releaseTimeoutRef.current) {
      clearTimeout(releaseTimeoutRef.current);
      releaseTimeoutRef.current = null;
    }
    if (frameRef.current !== null && typeof window !== "undefined") {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const applyVisualSpeaker = useCallback(
    (target: Speaker) => {
      const previous = visualSpeakerRef.current;
      const duration = getTransitionDuration(previous, target);
      visualSpeakerRef.current = target;
      setTransitionDuration(duration);
      setVisualSpeaker(target);
    },
    [setTransitionDuration, setVisualSpeaker]
  );

  const scheduleVisualState = useCallback(
    (target: Speaker, delayMs: number) => {
      clearVisualTimers();

      const runUpdate = () => {
        applyVisualSpeaker(target);
      };

      if (delayMs > 0) {
        releaseTimeoutRef.current =
          typeof window === "undefined"
            ? (setTimeout(() => {
                releaseTimeoutRef.current = null;
                runUpdate();
              }, delayMs) as ReturnType<typeof setTimeout>)
            : window.setTimeout(() => {
                releaseTimeoutRef.current = null;
                runUpdate();
              }, delayMs);
        return;
      }

      if (typeof window === "undefined") {
        setTimeout(runUpdate, 0);
        return;
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        runUpdate();
      });
    },
    [applyVisualSpeaker, clearVisualTimers]
  );

  useEffect(() => {
    if (speaker === "user" || speaker === "ai") {
      scheduleVisualState(speaker, 0);
      return;
    }

    scheduleVisualState("idle", IDLE_RELEASE_DELAY_MS);
  }, [speaker, scheduleVisualState]);

  useEffect(
    () => () => {
      clearVisualTimers();
    },
    [clearVisualTimers]
  );

  const getBackgroundClass = () => {
    if (status === "idle" || status === "stopped") {
      return "bg-gradient-idle";
    }

    switch (visualSpeaker) {
      case "user":
        return "bg-gradient-user";
      case "ai":
        return "bg-gradient-ai";
      default:
        return "bg-gradient-idle";
    }
  };

  const transitionStyle = useMemo<TransitionStyle>(
    () => ({
      "--bg-transition-duration": `${transitionDuration}ms`,
    }),
    [transitionDuration]
  );

  return (
    <VoiceWebSocketProvider>
      <div
        className={`relative h-screen w-screen flex flex-col bg-transition ${getBackgroundClass()}`}
        style={transitionStyle}
      >
        <UserAvatar />
        <div className="absolute top-12 left-0 w-full">
          <Header />
        </div>

        {/* Transcript Toggle Button */}
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={toggleShowTranscript}
            className="bg-white/20 text-black backdrop-blur-md border border-white/30 px-4 py-2 rounded-full hover:bg-white/30 transition-all text-sm font-medium shadow-sm"
          >
            {showTranscript ? (
              <div className="flex items-center justify-center gap-x-2">
                <CaptionsOff />
                <span>Hide Transcript</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-x-2">
                <Captions />
                <span>Show Transcript</span>
              </div>
            )}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center overflow-hidden">
          {showTranscript ? <TranscriptDisplay /> : <Blob />}
        </div>

        <Controls />
      </div>
    </VoiceWebSocketProvider>
  );
};

export default FiBiVoiceAgent;
